import { useEffect, useState } from 'react'
import { Settings, X, Minimize2, Maximize2 } from 'lucide-react'
import { publicChartingService } from '@/services/publicCharting'
import { resolveConfig, MTF_TIMEFRAMES, HTF_MAP } from './constants.js'
import { computeDirection } from './engine.js'

// Misma conversión que usePublicChartData (símbolo UI -> símbolo CCXT).
function toCcxtSymbol(raw) {
  if (!raw) return raw
  if (raw.includes('/') && raw.includes(':')) return raw
  if (raw.includes('/')) {
    const [base, quote] = raw.split('/')
    return `${base}/${quote}:${quote}`
  }
  let s = raw.replace(/\.P$/i, '').replace(/_/g, '-')
  if (s.includes('-')) {
    const [base, quote] = s.split('-')
    return `${base}/${quote}:${quote}`
  }
  const m = s.match(/^(.*?)(USDT|USDC|BTC|ETH|USD)$/i)
  if (m) return `${m[1]}/${m[2]}:${m[2]}`
  return raw
}

// El chart standalone no usa react-router: símbolo/TF se leen de la URL si
// están, o del meta global que la página escribe en cada render del registry.
// Si ninguna fuente está disponible, MTF y fila HTF se ocultan.
function getChartContext() {
  try {
    const params = new URLSearchParams(window.location.search)
    const symbol = params.get('symbol')
    const timeframe = params.get('timeframe') || params.get('tf')
    if (symbol && timeframe) return { symbol, timeframe }
  } catch { /* sin window */ }
  const meta = typeof window !== 'undefined' ? window.__lastRenderMeta : null
  if (meta?.symbol && meta?.timeframe) return { symbol: meta.symbol, timeframe: meta.timeframe }
  return null
}

async function fetchTfDirection(symbol, tf, cfg) {
  const now = Math.floor(Date.now() / 1000)
  const from = now - tf.seconds * 300
  const res = await publicChartingService.getHistory({
    symbol: toCcxtSymbol(symbol),
    resolution: tf.value,
    from_time: from,
    to_time: now,
    source: 'bingx',
    _t: Date.now(),
  })
  if (res.data?.s !== 'ok' || !Array.isArray(res.data.t) || res.data.t.length === 0) return 'undefined'
  let candles = res.data.t.map((time, i) => ({
    time,
    open: res.data.o[i],
    high: res.data.h[i],
    low: res.data.l[i],
    close: res.data.c[i],
  }))
  // No-repintado: la vela aún en formación se descarta.
  const last = candles[candles.length - 1]
  if (last && typeof last.time === 'number' && last.time + tf.seconds > now) {
    candles = candles.slice(0, -1)
  }
  return computeDirection(candles, cfg)
}

const DIR_LABEL = { long: 'Long', short: 'Short', transition: 'Transition', undefined: 'Indefinido' }

const TABLE_POSITION_STYLE = {
  'top-left': { top: 12, left: 12 },
  'top-right': { top: 12, right: 12 },
  'bottom-left': { bottom: 12, left: 12 },
  'bottom-right': { bottom: 12, right: 12 },
}

const TABLE_TEXT_CLASS = {
  small: 'text-[9px]',
  medium: 'text-[10px]',
  large: 'text-[11px]',
}

function fmtPrice(p) {
  if (!Number.isFinite(p)) return '—'
  const a = Math.abs(p)
  if (a >= 100) return p.toFixed(2)
  if (a >= 1) return p.toFixed(4)
  return p.toFixed(6)
}

function fmtTime(t) {
  if (!Number.isFinite(t)) return '—'
  const d = new Date(t * 1000)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toISOString().slice(5, 16).replace('T', ' ') + ' UTC'
}

export default function BootPullbackBadge({
  result,
  onOpenSettings,
  onClose,
  onMinimize,
  minimized = false,
  version = '1.0',
  onHeaderMouseDown,
}) {
  const cfg = resolveConfig(result?._cfg)
  const dash = result?.dashboard ?? {}
  const stateTable = result?.stateTable ?? null
  const error = result?.error ?? null

  const bias = dash.bias ?? 'undefined'
  const biasColor =
    bias === 'long' ? cfg.colorBull :
    bias === 'short' ? cfg.colorBear :
    bias === 'transition' ? cfg.colorAccent :
    cfg.colorNeutral

  const dirColor = (d) =>
    d === 'long' ? cfg.colorBull :
    d === 'short' ? cfg.colorBear :
    d === 'transition' ? cfg.colorAccent :
    cfg.colorNeutral

  // ── MTF: fetch perezoso con refresh periódico ────────────────────────────
  const [mtf, setMtf] = useState(null)
  const mtfWanted = (cfg.showMultiTimeframe || cfg.showStateTable) && !minimized && !error
  useEffect(() => {
    if (!mtfWanted) {
      setMtf(null)
      return undefined
    }
    let cancelled = false
    const load = async () => {
      const ctx = getChartContext()
      if (!ctx) {
        if (!cancelled) setMtf(null)
        return
      }
      try {
        const entries = await Promise.all(
          MTF_TIMEFRAMES.map(async (tf) => [tf.value, await fetchTfDirection(ctx.symbol, tf, cfg)])
        )
        if (cancelled) return
        const directions = Object.fromEntries(entries)
        const score = entries.reduce((acc, [, d]) => acc + (d === 'long' ? 1 : d === 'short' ? -1 : 0), 0)
        const htfTf = cfg.htfOverride !== 'auto' ? cfg.htfOverride : (HTF_MAP[ctx.timeframe] ?? null)
        setMtf({
          directions,
          score,
          htfTf,
          htfDir: htfTf ? (directions[htfTf] ?? 'undefined') : 'undefined',
        })
      } catch {
        if (!cancelled) setMtf(null)
      }
    }
    load()
    const id = setInterval(load, 45000)
    return () => {
      cancelled = true
      clearInterval(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mtfWanted, cfg.htfOverride, cfg.triggerOnWick, cfg.internalPivotLen])

  const textClass = TABLE_TEXT_CLASS[cfg.stateTableTextSize] ?? TABLE_TEXT_CLASS.small
  const lastEvent = dash.lastEvent

  return (
    <>
      <div
        className="absolute top-2 left-2 z-10 flex flex-col gap-1 rounded-lg backdrop-blur-sm shadow-lg overflow-hidden min-w-[200px]"
        style={{ background: 'var(--paper, #FDFDFA)', border: '1px solid var(--hair, rgba(176,141,87,0.35))' }}
      >
        <div
          className="flex items-center gap-1.5 px-2.5 pt-2 pb-1 cursor-grab active:cursor-grabbing"
          onMouseDown={onHeaderMouseDown}
        >
          <span
            className="text-[11px] font-semibold tracking-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", color: 'var(--green-deep, #0B3D2E)' }}
          >
            Boot Pullback <span className="opacity-60 font-normal">[{version}]</span>
            {minimized && (
              <span className="ml-1.5 text-[10px]" style={{ color: biasColor }}>· {DIR_LABEL[bias] ?? bias}</span>
            )}
          </span>
          <button onClick={onOpenSettings} title="Configuración" className="ml-auto p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
            <Settings size={12} />
          </button>
          {minimized ? (
            <button onClick={onMinimize} title="Expandir" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
              <Maximize2 size={12} />
            </button>
          ) : (
            <button onClick={onMinimize} title="Minimizar" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
              <Minimize2 size={12} />
            </button>
          )}
          <button onClick={onClose} title="Ocultar dashboard" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
            <X size={12} />
          </button>
        </div>

        {!minimized && (
          <>
            {error ? (
              <div className="px-2.5 pb-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>Error</span>
                  <span className="text-[10px] font-medium" style={{ color: cfg.colorBear }}>
                    {error.message || error.code || 'Datos inválidos'}
                  </span>
                </div>
              </div>
            ) : (
              <>
                <div className="px-2.5 pb-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>Sesgo</span>
                    <span className="text-[11px] font-semibold" style={{ color: biasColor }}>
                      {DIR_LABEL[bias] ?? bias}
                    </span>
                  </div>
                </div>

                <div className="px-2.5 pb-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>Swings externos</span>
                    <span className="text-[10px] font-medium" style={{ color: 'var(--ink, #15211B)' }}>{dash.externalSwings ?? 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>Rupturas CHoCH/BOS</span>
                    <span className="text-[10px] font-medium" style={{ color: 'var(--ink, #15211B)' }}>{dash.breaksCount ?? 0}</span>
                  </div>
                </div>

                {lastEvent && (
                  <div className="px-2.5 pb-2" style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }}>
                    <div className="pt-1 flex items-center justify-between">
                      <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>Último evento</span>
                      <span className="text-[10px] font-medium" style={{ color: lastEvent.dir === 'up' ? cfg.colorBull : cfg.colorBear }}>
                        {lastEvent.label} {lastEvent.dir === 'up' ? 'alcista' : 'bajista'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>{fmtPrice(lastEvent.price)}</span>
                      <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>{fmtTime(lastEvent.time)}</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Tabla de estado + panel MTF: contenedor fijo en la esquina elegida.
          Los datos salen de result.stateTable (misma fuente que el canvas). */}
      {!minimized && !error && (cfg.showStateTable || (cfg.showMultiTimeframe && mtf)) && (
        <div
          className="fixed z-10 flex flex-col gap-1"
          style={TABLE_POSITION_STYLE[cfg.stateTablePosition] ?? TABLE_POSITION_STYLE['top-right']}
        >
          {cfg.showStateTable && stateTable && (
            <div
              className={`flex flex-col gap-0.5 rounded-lg backdrop-blur-sm shadow-lg px-2.5 py-2 min-w-[210px] ${textClass}`}
              style={{ background: 'var(--paper, #FDFDFA)', border: '1px solid var(--hair, rgba(176,141,87,0.35))' }}
            >
              <TableRow
                label="Dirección"
                value={`${DIR_LABEL[stateTable.direction] ?? stateTable.direction} ${stateTable.directionSuffix || ''}`.trim()}
                color={dirColor(stateTable.direction)}
              />
              <TableRow
                label="Última estructura"
                value={stateTable.lastStructureDir ? (stateTable.lastStructureDir === 'up' ? 'Alcista' : 'Bajista') : '—'}
                color={stateTable.lastStructureDir === 'up' ? cfg.colorBull : stateTable.lastStructureDir === 'down' ? cfg.colorBear : undefined}
              />
              <TableRow label="Límite superior" value={fmtPrice(stateTable.extH?.price)} color={cfg.colorBull} />
              <TableRow label="Límite inferior" value={fmtPrice(stateTable.extL?.price)} color={cfg.colorBear} />
              <TableRow
                label="Límites anteriores"
                value={`${fmtPrice(stateTable.prevExtH?.price)} / ${fmtPrice(stateTable.prevExtL?.price)}`}
              />
              <TableRow
                label="Si rompe el superior"
                value={`${stateTable.ifBreakUp?.label ?? '—'}: ${stateTable.ifBreakUp?.effect ?? '—'}`}
              />
              <TableRow
                label="Si rompe el inferior"
                value={`${stateTable.ifBreakDown?.label ?? '—'}: ${stateTable.ifBreakDown?.effect ?? '—'}`}
              />
              <TableRow label="Condición de reversión" value={stateTable.reversalCondition ?? '—'} />
              {mtf && mtf.htfTf && (
                <TableRow
                  label={`Dirección HTF (${(MTF_TIMEFRAMES.find(t => t.value === mtf.htfTf)?.label) ?? mtf.htfTf})`}
                  value={DIR_LABEL[mtf.htfDir] ?? mtf.htfDir}
                  color={dirColor(mtf.htfDir)}
                />
              )}
            </div>
          )}

          {cfg.showMultiTimeframe && mtf && (
            <div
              className="flex flex-col gap-0.5 rounded-lg backdrop-blur-sm shadow-lg px-2.5 py-2 min-w-[170px]"
              style={{ background: 'var(--paper, #FDFDFA)', border: '1px solid var(--hair, rgba(176,141,87,0.35))' }}
            >
              <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: 'var(--gold, #B08D57)' }}>
                Multitemporal
              </span>
              {MTF_TIMEFRAMES.map(tf => {
                const d = mtf.directions?.[tf.value] ?? 'undefined'
                return (
                  <div key={tf.value} className="flex items-center justify-between">
                    <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>{tf.label}</span>
                    <span className="text-[9px] font-medium" style={{ color: dirColor(d) }}>
                      {DIR_LABEL[d] ?? d}
                    </span>
                  </div>
                )
              })}
              <div
                className="flex items-center justify-between pt-0.5"
                style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }}
              >
                <span className="text-[9px]" style={{ color: 'var(--ink, #15211B)' }}>Alignment Score</span>
                <span
                  className="text-[9px] font-semibold"
                  style={{ color: mtf.score > 0 ? cfg.colorBull : mtf.score < 0 ? cfg.colorBear : cfg.colorNeutral }}
                >
                  {mtf.score > 0 ? `+${mtf.score}` : mtf.score}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  )
}

function TableRow({ label, value, color }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span style={{ color: 'var(--ink-soft, #4A5A52)' }}>{label}</span>
      <span className="font-medium text-right" style={{ color: color || 'var(--ink, #15211B)' }}>{value}</span>
    </div>
  )
}
